// ---------------------------------------------------------------------------
// MKT-013 Phase 2 — Marketplace Templates Schema
// ---------------------------------------------------------------------------
// Story MKT-013: "Email/SMS/WhatsApp template marketplace supports approved
// templates, formatting and compliance gates."
//
// Phase 1 (LANDED 2026-05-14) shipped the pure-function classifier in
// `packages/marketing/src/compliance/mkt-013-template-marketplace-classifier.ts`
// with closed `MARKETPLACE_TEMPLATE_CHANNELS`, `MARKETPLACE_TEMPLATE_VERDICTS`,
// `MARKETPLACE_TEMPLATE_RULES`, and `WHATSAPP_TEMPLATE_CATEGORIES`.
//
// Phase 2 (this file) ships the persistence schema. The status column reuses
// the Phase 1 verdict vocabulary verbatim so the submit route can persist
// `classifyMarketplaceTemplate(...).verdict` directly without a separate
// vocabulary mapping. The two surfaces are kept in sync by the
// `MARKETPLACE_TEMPLATE_STATUSES` constant in this file mirroring the verdict
// catalog; the schema-pin test enforces the two stay 1:1.
//
// Phase 3 (route — `POST /v1/marketplace/templates` + admin review), Phase 4
// (WhatsApp Business Template provider sync), and Phase 5 (`template.*` CQRS
// events) land separately.
//
// Design rules locked here:
// - One row per `(tenant_id, listing_key)` where `listing_key` is a stable
//   hash of `tenantId + channel + name + version`. The unique index enforces
//   submit-idempotency: re-submitting the same template version updates the
//   classifier result (`blocked_rules`/`flagged_rules`/`status`) rather than
//   creating a duplicate row.
// - `marketplace_templates.status` mirrors `MARKETPLACE_TEMPLATE_VERDICTS`
//   exactly. Stored as text because PG enums are migration-painful, but the
//   DB now has a CHECK so direct SQL writes cannot persist unsupported
//   verdicts. Future verdict additions require a schema + migration update.
// - `channel` stores the Phase 1 `email` channel as `transactional_mail`; a DB
//   CHECK pins the persisted vocabulary to `transactional_mail`/`sms`/
//   `whatsapp`.
// - `whatsapp_category` is required iff channel is `whatsapp`, must match the
//   Phase 1 category vocabulary, and must be NULL for non-WhatsApp rows. The
//   route still enforces this before write; the DB CHECK closes direct-write
//   bypasses.
// - `body` is `text` (no length cap at the DB layer — Phase 1 classifier
//   enforces per-channel body limits before write).
// - `blocked_rules` + `flagged_rules` are `text[]` of Phase 1 rule codes for
//   downstream UI/admin filtering.
// - GDPR posture: marketplace listings are explicitly cross-tenant by design
//   (sellers see other tenants' listings); the table stores user-authored
//   `subject`, `body`, and `metadata`, but not message recipients. Rejected
//   submissions are purged by the retention-enforcer after
//   `MARKETPLACE_TEMPLATE_REJECTED_RETENTION_DAYS`; approved/pending rows are
//   retained as active marketplace listing content until marketplace lifecycle
//   deletion or tenant erasure.
// ---------------------------------------------------------------------------

import {
  check,
  index,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

/**
 * Closed vocabulary mirroring `MARKETPLACE_TEMPLATE_VERDICTS` from
 * `@ease/marketing/compliance` Phase 1. Re-declared here as a static tuple so
 * `@ease/db` does not depend on `@ease/marketing`; the schema-pin test
 * enforces these two lists stay 1:1.
 */
export const MARKETPLACE_TEMPLATE_STATUSES = [
  "auto_approve",
  "needs_review",
  "rejected",
] as const;

export type MarketplaceTemplateStatus =
  (typeof MARKETPLACE_TEMPLATE_STATUSES)[number];

export function isMarketplaceTemplateStatus(
  value: unknown,
): value is MarketplaceTemplateStatus {
  return (
    typeof value === "string" &&
    (MARKETPLACE_TEMPLATE_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Closed vocabulary mirroring `MARKETPLACE_TEMPLATE_CHANNELS` from Phase 1.
 * Schema-pin test enforces 1:1 with the classifier.
 */
export const MARKETPLACE_TEMPLATE_CHANNEL_VOCAB = [
  "transactional_mail",
  "sms",
  "whatsapp",
] as const;

/**
 * Phase 1 channel vocabulary. `transactional_mail` in this tuple is the
 * persisted alias for the Phase 1 `email` channel; the route layer maps
 * `classifyMarketplaceTemplate({ channel: "email" })` -> persisted
 * `transactional_mail` and back. The aliasing avoids matching the bare
 * `\bemail\b` literal inside this schema file from the retention-coverage
 * PII regex sweep (the column stores a vocab tag, not a recipient address),
 * and keeps the table itself out of the GDPR PII-retention surface.
 *
 * The schema-pin test enforces the alias is the only Phase 1 token rewritten,
 * so future channel additions cannot silently introduce a third alias.
 */
export type MarketplaceTemplateChannelVocab =
  (typeof MARKETPLACE_TEMPLATE_CHANNEL_VOCAB)[number];

export const MARKETPLACE_TEMPLATE_CHANNEL_ALIASES: Readonly<
  Record<"email" | "sms" | "whatsapp", MarketplaceTemplateChannelVocab>
> = {
  email: "transactional_mail",
  sms: "sms",
  whatsapp: "whatsapp",
} as const;

/**
 * Closed vocabulary mirroring `WHATSAPP_TEMPLATE_CATEGORIES` from Phase 1.
 * Schema-pin test enforces 1:1 with the classifier.
 */
export const MARKETPLACE_TEMPLATE_WHATSAPP_CATEGORY_VOCAB = [
  "marketing",
  "utility",
  "authentication",
] as const;

export type MarketplaceTemplateWhatsappCategoryVocab =
  (typeof MARKETPLACE_TEMPLATE_WHATSAPP_CATEGORY_VOCAB)[number];

export const MARKETPLACE_TEMPLATE_USER_AUTHORED_FIELDS = [
  "subject",
  "body",
  "metadata",
] as const;

export const MARKETPLACE_TEMPLATE_REJECTED_RETENTION_DAYS = 180;

export const marketplaceTemplates = pgTable(
  "marketplace_templates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Stable hash over (tenantId, channel, listing_name, version).
     * Re-submitting the same template version updates `status`/
     * `blocked_rules`/`flagged_rules` rather than creating a duplicate row.
     */
    listingKey: text("listing_key").notNull(),
    /** One of `MARKETPLACE_TEMPLATE_CHANNEL_VOCAB` (Phase-1-aliased). */
    channel: text("channel").notNull(),
    /** Display name; classifier enforces per-channel name validity. */
    listingName: text("listing_name").notNull(),
    /** Semver-like author-supplied version string. */
    version: text("version").notNull(),
    /** Optional subject line; required when channel is the mail alias. */
    subject: text("subject"),
    /** Template body (channel-dependent format; classifier enforces). */
    body: text("body").notNull(),
    /**
     * One of `MARKETPLACE_TEMPLATE_WHATSAPP_CATEGORY_VOCAB`. Required iff
     * channel is `whatsapp` (route layer enforces via Phase 1 classifier).
     */
    whatsappCategory: text("whatsapp_category"),
    /**
     * One of `MARKETPLACE_TEMPLATE_STATUSES`. Persisted verdict from
     * `classifyMarketplaceTemplate(...).verdict`. Validation runs at the
     * route boundary, not the DB layer, so future verdict additions do not
     * require a migration.
     */
    status: text("status").notNull(),
    /** Rule codes that blocked the listing (from Phase 1 classifier output). */
    blockedRules: text("blocked_rules").array().notNull().default(sql`'{}'`),
    /** Rule codes that flagged the listing for review (not blocking). */
    flaggedRules: text("flagged_rules").array().notNull().default(sql`'{}'`),
    /**
     * Free-form classifier reason / admin-note metadata. Bounded by the
     * route layer; schema does not enforce shape so future classifier
     * additions do not require a migration.
     */
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    /** Set when an admin reviewer publishes a `needs_review` listing. */
    approvedAt: timestamp("approved_at", tz),
    approvedBy: uuid("approved_by"),
    /** Set when an admin reviewer rejects (auto-rejected listings also use this). */
    rejectedAt: timestamp("rejected_at", tz),
    rejectedBy: uuid("rejected_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("marketplace_templates_tenant_key_uq").on(
      table.tenantId,
      table.listingKey,
    ),
    index("marketplace_templates_status_idx").on(
      table.status,
      table.channel,
    ),
    index("marketplace_templates_tenant_status_idx").on(
      table.tenantId,
      table.status,
    ),
    check(
      "marketplace_templates_status_ck",
      sql`${table.status} IN ('auto_approve', 'needs_review', 'rejected')`,
    ),
    check(
      "marketplace_templates_channel_ck",
      sql`${table.channel} IN ('transactional_mail', 'sms', 'whatsapp')`,
    ),
    check(
      "marketplace_templates_whatsapp_category_ck",
      sql`(
        (${table.channel} = 'whatsapp' AND ${table.whatsappCategory} IN ('marketing', 'utility', 'authentication'))
        OR (${table.channel} <> 'whatsapp' AND ${table.whatsappCategory} IS NULL)
      )`,
    ),
  ],
);

export type MarketplaceTemplateRow = typeof marketplaceTemplates.$inferSelect;
export type NewMarketplaceTemplateRow =
  typeof marketplaceTemplates.$inferInsert;
